import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useToast } from '../contexts/ToastContext';
import { getStateOptions, getStateByCode } from '../config/states';
import { getTotalItemsForState } from '../config/checklist';
import { createSnapshot, trackEvent } from '../lib/api';

export function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { error: showError } = useToast();

  // Get state from URL params if passed from preview page
  const initialState = searchParams.get('state') || '';

  const [email, setEmail] = useState('');
  const [selectedState, setSelectedState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ state?: string; email?: string }>({});

  // Scroll to top on mount and track page view
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent({
      event: 'checkout_page_view',
      properties: {
        initialState: initialState || null,
        source: 'direct',
      },
    });
  }, []);

  const stateOptions = getStateOptions();
  const selectedStateInfo = selectedState ? getStateByCode(selectedState) : null;
  const totalItems = selectedState ? getTotalItemsForState(selectedState) : 50;

  const handleCheckout = async () => {
    // Inline errors next to the fields — the corner toast alone reads as a
    // dead button, especially on mobile.
    const errors: { state?: string; email?: string } = {};
    if (!selectedState) errors.state = 'Please select your state';
    if (!email || !email.includes('@')) errors.email = 'Please enter a valid email address';
    setFieldErrors(errors);
    if (errors.state || errors.email) {
      showError(errors.state || errors.email || 'Please complete the form');
      return;
    }

    setIsLoading(true);

    // Track assessment start
    trackEvent({
      event: 'free_assessment_started',
      properties: {
        selectedState,
        source: 'direct',
      },
    });

    try {
      // Create snapshot (status is set to 'paid' on backend, skipping Stripe).
      // The old ?snapshotId= shortcut is gone on purpose: a raw snapshot ID in
      // a URL is an access credential. Personalized links use /i/<token>.
      const snapshotResult = await createSnapshot(email, selectedState, totalItems);

      // Go directly to assessment
      navigate(`/assessment?snapshotId=${snapshotResult.snapshotId}`);
    } catch (err) {
      console.error('Checkout error:', err);
      showError(err instanceof Error ? err.message : 'Failed to start assessment. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-8 pt-24 md:py-12 md:pt-28">
        <Container size="lg">
          {/* Value banner — lead with the benefit, not fear */}
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-lg" style={{ backgroundColor: 'rgba(0, 150, 136, 0.06)', border: '1px solid rgba(0, 150, 136, 0.25)' }}>
            <div className="flex items-start gap-2 sm:gap-3">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{ color: '#009688' }} />
              <div>
                <p className="font-semibold text-sm sm:text-base" style={{ color: '#0f766e' }}>
                  {selectedStateInfo
                    ? `Know exactly where ${selectedStateInfo.name} agencies get flagged`
                    : 'Know exactly where surveyors flag agencies'}
                </p>
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#0f766e' }}>
                  A free, state-specific check of the same personnel-file items a surveyor reviews — so you find the gaps before they do.
                </p>
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Product Info */}
            <div className="order-2 space-y-6 lg:order-1">
              {/* Product Header */}
              <div>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#009688' }}>
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                      {selectedStateInfo
                        ? `${selectedStateInfo.name} Compliance Risk Snapshot`
                        : 'Compliance Risk Snapshot'}
                    </h1>
                    <p className="text-sm sm:text-base text-gray-500">
                      {selectedStateInfo
                        ? `Assessment based on ${selectedStateInfo.regulatoryBody} requirements`
                        : 'State-specific assessment for home care agencies'}
                    </p>
                  </div>
                </div>
              </div>

              {/* What's included */}
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">
                    What's included:
                  </h3>
                  <ul className="space-y-2 sm:space-y-3">
                    {[
                      selectedStateInfo
                        ? `State-specific diagnostic based on ${selectedStateInfo.name}'s current regulations`
                        : 'State-specific diagnostic based on your state\'s current regulations',
                      'Compliance Risk Score with category breakdown',
                      'Personnel file gap analysis — see exactly what\'s missing',
                      'Priority action list ranked by audit risk',
                      'Fix gaps before your next survey — not after',
                      'Shareable PDF report with regulation citations',
                      'Resume anytime (saves progress)',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 sm:gap-3">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* State-specific compliance risks — only truly state-varying info */}
              {selectedStateInfo && (
                <div className="p-4 sm:p-5 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700 shrink-0" />
                    <span className="font-semibold text-amber-800 text-sm sm:text-base">
                      What {selectedStateInfo.name} requires:
                    </span>
                  </div>
                  <ul className="text-amber-700 space-y-2 text-sm sm:text-base">
                    <li className="flex items-start gap-2">
                      <span className="font-semibold shrink-0">{selectedStateInfo.training.initialHours} hrs</span>
                      <span>initial caregiver training</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold shrink-0">{selectedStateInfo.training.annualHours} hrs</span>
                      <span>annual in-service training</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold shrink-0 text-xs sm:text-sm">Regulator:</span>
                      <span className="text-xs sm:text-sm">
                        {selectedStateInfo.regulatoryBody} ({selectedStateInfo.regulatoryBodyAbbr})
                      </span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Right Column - Checkout Form */}
            <div className="order-1 lg:order-2">
              <Card className="overflow-hidden lg:sticky lg:top-28">
                {/* Header */}
                <div className="text-white px-4 sm:px-6 py-4 sm:py-6 text-center" style={{ backgroundColor: '#009688' }}>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Free Assessment</div>
                  <p className="text-sm sm:text-base" style={{ color: 'rgba(255,255,255,0.8)' }}>Find your compliance gaps in 15 minutes</p>
                </div>

                <CardContent className="p-4 sm:p-6">
                  {/* State selector */}
                  <div className="mb-4">
                    <Select
                      label="Your State"
                      value={selectedState}
                      onChange={e => {
                        setSelectedState(e.target.value);
                        setFieldErrors(prev => ({ ...prev, state: undefined }));
                      }}
                      error={fieldErrors.state}
                      options={[
                        { value: '', label: 'Select your state' },
                        ...stateOptions,
                      ]}
                    />
                  </div>

                  {/* Email input */}
                  <div className="mb-6">
                    <Input
                      label="Your Email"
                      type="email"
                      placeholder="you@youragency.com"
                      value={email}
                      onChange={e => {
                        setEmail(e.target.value);
                        setFieldErrors(prev => ({ ...prev, email: undefined }));
                      }}
                      error={fieldErrors.email}
                      helpText="We'll send your PDF report to this email"
                    />
                  </div>

                  {/* Start button */}
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleCheckout}
                    isLoading={isLoading}
                    leftIcon={<Shield className="w-5 h-5" />}
                  >
                    Start Free Assessment
                  </Button>

                  {/* Note */}
                  <p className="mt-3 text-xs text-center text-gray-500">
                    No payment required. See your risk score the moment you finish — full PDF report emailed to you.
                  </p>

                  {/* Back link */}
                  <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                    <Button
                      variant="ghost"
                      leftIcon={<ArrowLeft className="w-4 h-4" />}
                      onClick={() => {
                        // Track abandonment
                        trackEvent({
                          event: 'checkout_back_to_preview',
                          properties: {
                            selectedState: selectedState || null,
                            hasEmail: !!email,
                            source: searchParams.get('snapshotId') ? 'invite' : 'direct',
                          },
                        });
                        navigate('/preview');
                      }}
                      className="w-full sm:w-auto min-h-[44px]"
                    >
                      Back to Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Support */}
              <div className="mt-4 text-center text-sm text-gray-500">
                <p>
                  Questions? Contact{' '}
                  <a
                    href="mailto:support@getasili.com"
                    className="hover:opacity-80"
                    style={{ color: '#009688' }}
                  >
                    support@getasili.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
}
